import { useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import { AuthLayout, authFieldSx } from '@dikonek/ui-common';
import { opsApi, OpsApiError } from '../api/client';
import logo from '../assets/dikonek dark mode.png';

/**
 * Self-service "forgot password" — collects an email and asks the backend to
 * mail a reset link. The response is intentionally generic (we never confirm
 * whether the address has an account), so on success we always show the same
 * "check your email" panel.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await opsApi.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(
        err instanceof OpsApiError && err.serverMessage
          ? err.serverMessage
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      appName="Reset password"
      subtitle="We'll email you a reset link."
      logo={logo}
      logoHeight={120}
    >
      {sent ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, textAlign: 'center' }}>
          <Alert
            severity="success"
            variant="outlined"
            sx={{
              borderRadius: 2,
              color: '#a5d6a7',
              borderColor: 'rgba(76,175,80,0.4)',
              backgroundColor: 'rgba(76,175,80,0.08)',
              textAlign: 'left',
            }}
          >
            If an account exists for that email, a password reset link is on its way. The link
            expires in 1 hour.
          </Alert>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.45)',
              textDecorationColor: 'rgba(255,255,255,0.2)',
              '&:hover': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Back to sign in
          </Link>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
        >
          {error && (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                borderRadius: 2,
                color: '#ff8a80',
                borderColor: 'rgba(244,67,54,0.4)',
                backgroundColor: 'rgba(244,67,54,0.08)',
              }}
            >
              {error}
            </Alert>
          )}
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
            Enter the email address on your account and we'll send you a link to reset your
            password.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={authFieldSx}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} /> : undefined}
            sx={{
              mt: 1,
              py: 1.4,
              fontSize: 15,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1565c0 0%, #5e92f3 100%)',
              boxShadow: '0 4px 20px rgba(21,101,192,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #003c8f 0%, #1565c0 100%)',
                boxShadow: '0 6px 24px rgba(21,101,192,0.5)',
              },
              '&.Mui-disabled': {
                background: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            {busy ? 'Sending...' : 'Send reset link'}
          </Button>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.45)',
              textDecorationColor: 'rgba(255,255,255,0.2)',
              '&:hover': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Back to sign in
          </Link>
        </Box>
      )}
    </AuthLayout>
  );
}
